/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { motion, AnimatePresence } from 'framer-motion'; // Import motion and AnimatePresence
import { ArrowLeftCircleIcon, ArrowRightCircleIcon } from '@heroicons/react/24/outline';
import Divider from '@/components/Divider';
import { useTraitCalendar } from './useTraitCalendar';
import useIsMobile from '@/hooks/useIsMobile';
import { useRef, useState } from 'react';
import DialogTraitDetail, {
  DialogCharacterCompassDetailRef,
} from '@/pages/character-compass/dialog-character-compass-detail';
import { useCharacterCompassStore } from '@/pages/character-compass/store/useCharacterCompassStore';
import { classNames } from '@/utils/class-names';

export const Calendar = ({ isCharacterCompassPage }: { isCharacterCompassPage?: boolean }) => {
  const { currentMonth, days, handlePrevMonth, handleNextMonth, page } = useTraitCalendar();

  const { characterCompassPage } = useCharacterCompassStore();

  const { isMobile } = useIsMobile();
  const [traitDetail, setTraitDetail] = useState<any>({
    emoji: '',
    title: '',
    description: '',
  });

  const dialogRef = useRef<DialogCharacterCompassDetailRef>(null);

  const onClickDay = (type: 'not-recorded' | 'recorded', data?: any) => {
    if (!isMobile) return;
    if (type === 'not-recorded') {
      setTraitDetail({
        emoji: page?.traitTrackingSection?.notRecordedIcon.data?.attributes?.url,
        title: page?.traitTrackingSection?.notRecordedLabel,
        description: page?.traitTrackingSection?.notRecordedDescription,
      });

      return dialogRef.current?.openDialog();
    }

    setTraitDetail({
      emoji: data?.emoji,
      title: data?.label,
      description: data?.description,
    });

    return dialogRef.current?.openDialog();
  };

  const renderHeader = () => (
    <div className="mb-4 flex items-center justify-center gap-3">
      <button className="text-black hover:text-gray-500" onClick={handlePrevMonth}>
        <ArrowLeftCircleIcon className="h-6 w-6" />
      </button>
      <h2 className="text-lg font-semibold">{currentMonth.format('MMMM YYYY')}</h2>
      <button className="text-black hover:text-gray-500" onClick={handleNextMonth}>
        <ArrowRightCircleIcon className="h-6 w-6" />
      </button>
    </div>
  );

  return (
    <div className="mt-sh-60">
      {!isCharacterCompassPage && <h1 className="text-sh-md font-bold">Trait Tracking</h1>}
      {isCharacterCompassPage && (
        <h1 className=" text-center font-cadiz text-sh-20-28 ">{characterCompassPage?.calendarTitle}</h1>
      )}
      <div className="mt-5 rounded-2xl border border-primary-500 bg-white p-5">
        {renderHeader()}
        <Divider />
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={currentMonth.format('MMMM YYYY')}
            initial={{ opacity: 1 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-7 bg-white text-center text-gray-700">
            {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((d) => (
              <div key={d} className="py-1 font-bold">
                {d}
              </div>
            ))}
            {days.length > 0 &&
              days.map(({ day, emoji, isDisabledTracking, isCurrentMonth, label, ...rest }, index) => {
                const isToday = day === new Date().getDate() && currentMonth.isSame(new Date(), 'month');
                return (
                  <motion.div
                    key={index}
                    className={`mt-sh-15 flex flex-col items-center justify-start ${isCurrentMonth ? 'text-black' : 'text-gray-400'}`}
                    style={{ opacity: isCurrentMonth ? 1 : 0.5 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.01 }}>
                    <div className="text-sm font-medium">{day}</div>
                    {!isDisabledTracking &&
                      page?.traitTrackingSection?.notRecordedIcon.data?.attributes?.url &&
                      emoji && (
                        <img
                          className={classNames(
                            ' md:mt-sh-5 md:size-24',
                            isToday ? 'rounded-full border-2 border-[#1775E3] p-0.5' : '',
                          )}
                          src={emoji}
                          alt="trait"
                          onClick={() =>
                            onClickDay('recorded', { day, emoji, isDisabledTracking, isCurrentMonth, label, ...rest })
                          }
                          style={{ opacity: !isCurrentMonth ? '0.5' : '' }}
                        />
                      )}
                    {!isDisabledTracking && !emoji && (
                      <div className="size-8 rounded-full bg-gray-300 md:mt-sh-15 md:size-24"></div>
                    )}
                    {emoji && !isMobile && <p className=" mb-4 mt-2 text-sh-xxs font-medium ">{label}</p>}
                    {!emoji && !isMobile && (
                      <p className=" mb-7 mt-sh-15 text-sh-xxs font-medium ">{label || <>&nbsp;</>}</p>
                    )}
                  </motion.div>
                );
              })}
            {days.length === 0 &&
              Array.from({ length: 42 - days.length }).map((_, index) => (
                <div className="m-2 flex flex-col items-center justify-center" key={index}>
                  <div className="h-4 w-1/4 animate-pulse bg-gray-300 text-sm font-medium"></div>
                  <div key={index} className="flex justify-center">
                    <div className="mt-6 size-8 animate-pulse rounded-full  border-4 border-dashed border-gray-300 md:size-14"></div>
                  </div>
                  <p className=" mb-4 mt-2 h-4 w-full animate-pulse bg-gray-300 text-sh-xxs font-medium"></p>
                </div>
              ))}
          </motion.div>
        </AnimatePresence>
      </div>
      {isCharacterCompassPage && (
        <div
          className="mt-sh-30 text-center [&>p]:text-sh-xxs"
          dangerouslySetInnerHTML={{
            __html: characterCompassPage?.bottomText || '',
          }}
        />
      )}
      {/* <Button className="mt-5" onClick={onChangeTraitTrack} loading={isLoading}>
        {disableTraitTracking
          ? page?.traitTrackingSection?.btnStartTrackingLabel
          : page?.traitTrackingSection?.btnStopTrackingLabel}
      </Button> */}
      <DialogTraitDetail ref={dialogRef} detail={traitDetail} />
    </div>
  );
};
